import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatMoney, formatDate } from '../utils/helpers';

const isFullyPaid = p => (Number(p.total) > 0 && Number(p.paid_sum) >= Number(p.total))
  || (Number(p.total_count) > 0 && Number(p.paid_count) === Number(p.total_count));

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState('bankrot');
  const [civil, setCivil] = useState(null);
  const [open, setOpen] = useState({ paused: false, done: false, excluded: false });
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/payments').then(r => setPayments(r.data));
    api.get('/cases/payments/summary').then(r => setCivil(r.data)).catch(()=>{});
  }, []);

  // 11.10.2026: клиенты с пометкой «не учитывать в статистике» здесь считаются наравне со всеми — деньги показываем целиком
  // (пометка по-прежнему действует в воронке и конверсиях «Аналитики»)
  const done = payments.filter(p => isFullyPaid(p));
  // Просроченные первыми (на паузе — после них), дальше по дате следующего платежа
  const rank = p => Number(p.overdue_count) > 0 ? (p.payments_paused ? 1 : 0) : 2;
  // клиенты на паузе в основной таблице не показываются — они в свёрнутой группе «На паузе» и вернутся сами, когда паузу снимут
  const paused = payments.filter(p => !isFullyPaid(p) && p.payments_paused);
  const active = payments.filter(p => !isFullyPaid(p) && !p.payments_paused).sort((a, b) =>
    rank(a) - rank(b)
    || (rank(a) < 2 ? Number(b.overdue_sum) - Number(a.overdue_sum) : 0)
    || String(a.next_pay_date || '9999').localeCompare(String(b.next_pay_date || '9999')));

  // итоги — только по работающим рассрочкам; клиенты на паузе остаются в таблице с пометкой, но в суммы не идут
  const live = active;
  const totalSum = live.reduce((s,p) => s+Number(p.total||0), 0);
  const paidSum = live.reduce((s,p) => s+Number(p.paid_sum||0), 0);
  const overdue = active.filter(p => Number(p.overdue_count) > 0 && !p.payments_paused);
  const overdueSum = overdue.reduce((s,p) => s+Number(p.overdue_sum||0), 0);

  const group = (key, title, list) => list.length > 0 && (
    <div className="card" style={{marginTop:16}}>
      <div onClick={()=>setOpen(o=>({...o,[key]:!o[key]}))} style={{cursor:'pointer',fontSize:14,fontWeight:600,color:'var(--text-secondary)',userSelect:'none'}}>
        {open[key] ? '▾' : '▸'} {title} <span style={{color:'var(--text-muted)',fontWeight:400}}>({list.length})</span>
      </div>
      {open[key] && <div style={{marginTop:12}}><PaymentsTable rows={list} navigate={navigate} /></div>}
    </div>
  );

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Рассрочки</h1></div>
      <div className="tabs">
        <button className={`tab ${tab==='bankrot'?'active':''}`} onClick={()=>setTab('bankrot')}>Банкротство</button>
        <button className={`tab ${tab==='civil'?'active':''}`} onClick={()=>setTab('civil')}>
          Гражданские дела{civil && civil.cases.some(c=>c.overdue_count>0 && !c.paused) ? <span style={{marginLeft:6,color:'var(--red)',fontWeight:700}}>●</span> : null}
        </button>
      </div>
      {tab === 'civil' ? <CivilPayments data={civil} navigate={navigate} /> : <>
      <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'}}>
        <div className="card" style={{flex:1,minWidth:150,textAlign:'center'}}><div style={{fontSize:28,fontWeight:700}}>{live.length}</div><div style={{fontSize:13,color:'var(--text-secondary)'}}>Действующих рассрочек</div></div>
        <div className="card" style={{flex:1,minWidth:150,textAlign:'center'}}><div style={{fontSize:28,fontWeight:700,color:'var(--accent)'}}>{formatMoney(totalSum)}</div><div style={{fontSize:13,color:'var(--text-secondary)'}}>Общая сумма</div></div>
        <div className="card" style={{flex:1,minWidth:150,textAlign:'center'}}><div style={{fontSize:28,fontWeight:700,color:'var(--green)'}}>{formatMoney(paidSum)}</div><div style={{fontSize:13,color:'var(--text-secondary)'}}>Оплачено</div></div>
        <div className="card" style={{flex:1,minWidth:150,textAlign:'center'}}><div style={{fontSize:28,fontWeight:700,color:'var(--accent)'}}>{formatMoney(totalSum - paidSum)}</div><div style={{fontSize:13,color:'var(--text-secondary)'}}>Остаток к оплате</div></div>
        {overdue.length > 0 && (
          <div className="card" style={{flex:1,minWidth:150,textAlign:'center',borderLeft:'3px solid #e53e3e'}}>
            <div style={{fontSize:28,fontWeight:700,color:'#e53e3e'}}>{formatMoney(overdueSum)}</div>
            <div style={{fontSize:13,color:'var(--text-secondary)'}}>Просрочено ({overdue.length} клиент.)</div>
          </div>
        )}
      </div>
      <div className="card"><PaymentsTable rows={active} navigate={navigate} empty="Нет действующих рассрочек" /></div>
      {group('paused', 'На паузе', paused)}
      {group('done', 'Оплачено полностью', done)}
      </>}
    </div>
  );
}

// Напоминание о просрочке для MAX. У MAX нет ссылки «открыть чат по номеру с готовым текстом»,
// поэтому кнопка копирует текст (обращение по имени-отчеству, сумма, дата просрочки) и открывает MAX — остаётся вставить в чат клиента.
function remindText(p) {
  const parts = String(p.client_name || '').trim().split(/\s+/);
  const hello = parts.length >= 3 ? `${parts[1]} ${parts[2]}` : parts.length === 2 ? parts[1] : (parts[0] || '');
  return `${hello ? hello + ', з' : 'З'}дравствуйте! Это Эмиль, юридический центр «Фемида». Напоминаю о платеже по договору: ${formatMoney(p.overdue_sum)}`
    + `${p.overdue_since ? ', срок был ' + formatDate(p.overdue_since) : ''}. Подскажите, пожалуйста, когда сможете внести?`;
}

function RemindMax({ p }) {
  const [copied, setCopied] = useState(false);
  const go = async e => {
    e.stopPropagation();
    const text = remindText(p);
    try { await navigator.clipboard.writeText(text); }
    catch { const t = document.createElement('textarea'); t.value = text; document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove(); }
    setCopied(true);
    window.open('https://web.max.ru/', '_blank', 'noopener');
  };
  return (
    <div onClick={e => e.stopPropagation()}>
      <button type="button" onClick={go} style={{fontSize:12,fontWeight:500,color:'#2563eb',textDecoration:'underline',background:'none',border:'none',padding:0,cursor:'pointer',fontFamily:'inherit'}}>
        {copied ? 'текст скопирован — вставьте в чат MAX' : 'напомнить в MAX'}
      </button>
      {copied && p.client_phone && <div style={{fontSize:11,color:'var(--text-muted)',fontWeight:400}}>{p.client_phone}</div>}
    </div>
  );
}

function PaymentsTable({ rows, navigate, empty }) {
  return (
    <div className="table-wrap"><table><thead><tr><th>Клиент</th><th>Сумма</th><th>Оплачено</th><th>Прогресс</th><th>Следующий платёж</th><th>Просрочка</th></tr></thead><tbody>
      {rows.map(p => { const pct = Number(p.total) ? Math.min(Number(p.paid_sum) / Number(p.total) * 100, 100) : 0; const od = Number(p.overdue_count) > 0; return (
        <tr key={p.id} style={{cursor:'pointer'}} onClick={()=>navigate(`/clients/${p.client_id}?tab=payments`)}>
          <td><strong>{p.client_name}</strong>{p.payments_paused && <div style={{fontSize:11,color:'#f59e0b'}}>⏸ {p.payments_paused_note || 'на паузе'}</div>}</td>
          <td>{formatMoney(p.total)}</td>
          <td>{formatMoney(p.paid_sum)} <span style={{color:'var(--text-muted)',fontSize:12}}>({p.paid_count}/{p.total_count})</span></td>
          <td style={{width:180}}><div className="progress-bar"><div className="progress-fill" style={{width:`${pct}%`}} /></div><div style={{fontSize:11,color:'var(--text-muted)'}}>{Math.round(pct)}%</div></td>
          <td>{p.next_pay_date ? formatDate(p.next_pay_date) : '—'}</td>
          <td style={{color:od?(p.payments_paused?'#f59e0b':'#e53e3e'):'var(--text-muted)',fontWeight:od?600:400}}>{od ? `${formatMoney(p.overdue_sum)} (${p.overdue_count})` : '—'}
            {od && !p.payments_paused && <RemindMax p={p} />}</td>
        </tr>); })}
      {!rows.length && <tr><td colSpan={6}><div className="empty-state"><p>{empty || 'Нет'}</p></div></td></tr>}
    </tbody></table></div>
  );
}

function CivilPayments({ data, navigate }) {
  if (!data) return <div className="empty-state">Загрузка…</div>;
  const rows = data.cases;
  return (
    <div>
      <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'}}>
        <div className="card" style={{flex:1,minWidth:150,textAlign:'center'}}><div style={{fontSize:28,fontWeight:700,color:'var(--green)'}}>{formatMoney(data.month_paid)}</div><div style={{fontSize:13,color:'var(--text-secondary)'}}>Получено в этом месяце</div></div>
        <div className="card" style={{flex:1,minWidth:150,textAlign:'center'}}><div style={{fontSize:28,fontWeight:700,color:'var(--accent)'}}>{formatMoney(data.month_expected)}</div><div style={{fontSize:13,color:'var(--text-secondary)'}}>Ожидается в этом месяце</div></div>
        <div className="card" style={{flex:1,minWidth:150,textAlign:'center',borderLeft:Number(data.overdue)?'3px solid #e53e3e':undefined}}><div style={{fontSize:28,fontWeight:700,color:Number(data.overdue)?'#e53e3e':'var(--text)'}}>{formatMoney(data.overdue)}</div><div style={{fontSize:13,color:'var(--text-secondary)'}}>Просрочено</div></div>
      </div>
      <div className="card">
        <div className="table-wrap"><table><thead><tr><th>Клиент</th><th>Гонорар</th><th>Оплачено</th><th>Прогресс</th><th>Следующий платёж</th><th>Просрочка</th></tr></thead><tbody>
          {rows.map(r => { const pct = Number(r.scheduled) ? Number(r.paid_sum) / Number(r.scheduled) * 100 : 0; return (
            <tr key={r.id} style={{cursor:'pointer',opacity:r.paused?.6:1}} onClick={()=>navigate(`/cases/${r.id}`)}>
              <td><strong>{r.client_name}</strong>{r.paused && <div style={{fontSize:11,color:'#f59e0b'}}>⏸ на паузе — не учитывается{r.paused_note ? `: ${r.paused_note}` : ''}</div>}<div style={{fontSize:11,color:'var(--text-muted)'}}>{r.phone||''}</div></td>
              <td>{formatMoney(r.fee)}</td>
              <td>{formatMoney(r.paid_sum)} <span style={{color:'var(--text-muted)',fontSize:12}}>({r.paid_count}/{r.total_count})</span></td>
              <td style={{width:180}}><div className="progress-bar"><div className="progress-fill" style={{width:`${pct}%`}} /></div></td>
              <td>{r.next_pay_date ? formatDate(r.next_pay_date) : '—'}</td>
              <td style={{color:r.overdue_count?(r.paused?'#f59e0b':'#e53e3e'):'var(--text-muted)',fontWeight:r.overdue_count?600:400}}>{r.overdue_count ? `${formatMoney(r.overdue_sum)} (${r.overdue_count})` : '—'}</td>
            </tr>); })}
          {!rows.length && <tr><td colSpan={6}><div className="empty-state"><p>Графиков оплаты по гражданским делам пока нет. Их составляют во вкладке «Оплата» карточки дела.</p></div></td></tr>}
        </tbody></table></div>
      </div>
    </div>
  );
}
